import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { compose } from 'redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, CircularProgress } from '@material-ui/core';
import PropTypes from 'prop-types';
import {
  GET_ONE,
  CREATE,
  showNotification,
  fetchStart,
  fetchEnd,
  withDataProvider,
  translate as translateHOC,
} from 'react-admin';
import { MAX_NOTE_LENGTH } from './constants';
import TripNotesField from './TripNotesField';

const TripNotesDetailsDialog = memo(
  ({ open, onClose, tripId, dataProvider, translate, dispatch, readOnly, context }) => {
    const [note, setNote] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [validationError, setValidationError] = useState(null);

    const originalNote = useRef(null);

    const fetchTripNote = useCallback(async () => {
      setLoading(true);
      setError(null);
      try {
        const currentContext = context || 'BILLING';
        const response = await dataProvider(GET_ONE, 'tripNotes', { id: tripId, context: currentContext });
        let tripNote = '';

        if (currentContext === 'ONGOING_TRIP') {
          tripNote = response.data && response.data.content ? response.data.content : '';
        } else {
          tripNote =
            response.data && response.data.additionalInfo && response.data.additionalInfo.notes
              ? response.data.additionalInfo.notes
              : '';
        }

        setNote((tripNote || '').trim());
        originalNote.current = tripNote;
      } catch (err) {
        console.error('Error fetching trip note:', err);
        if (err.message && !err.message.includes('404')) {
          dispatch(
            showNotification('notifications.billing.tripNotes.loadFailed', 'warning', {
              messageArgs: { error_message: err.message },
            })
          );
        }
      } finally {
        setLoading(false);
      }
    }, [dataProvider, tripId, context]);

    useEffect(() => {
      if (open && tripId) {
        fetchTripNote();
      }
      // Reset state when dialog closes
      if (!open) {
        setNote('');
        setError(null);
        setValidationError(null);
        originalNote.current = null;
      }
    }, [open, tripId, fetchTripNote]);

    const validateNote = value => {
      const trimmed = value.trim();
      if (trimmed.length > MAX_NOTE_LENGTH) {
        return translate('validation.maxLength', { max: MAX_NOTE_LENGTH });
      }
      return null;
    };

    const handleNoteChange = e => {
      const newValue = e.target.value;
      setNote(newValue);

      const validationErr = validateNote(newValue);
      setValidationError(validationErr);
    };

    const handleSave = async () => {
      const trimmedNote = note.trim();

      const validationErr = validateNote(trimmedNote);
      if (validationErr) {
        setValidationError(validationErr);
        return;
      }

      setSaving(true);
      setError(null);
      setValidationError(null);
      dispatch(fetchStart());
      try {
        await dataProvider(CREATE, 'tripNotes', {
          id: tripId,
          data: { content: trimmedNote },
        });
        dispatch(showNotification('notifications.billing.tripNotes.saved', 'success'));
        dispatch(fetchEnd());
        onClose();
      } catch (err) {
        console.error('Error saving trip note:', err);
        dispatch(fetchEnd());
        dispatch(
          showNotification('notifications.billing.tripNotes.saveFailed', 'warning', {
            messageArgs: { error_message: err.message },
          })
        );
      } finally {
        setSaving(false);
      }
    };

    const isNoteValid = () => {
      return !validationError && note.trim().length <= MAX_NOTE_LENGTH;
    };

    if (!open) return null;

    const dialogTitle = translate('resources.billing.tripNotes.title', { tripId });

    const isSaveButtonDisabled = saving || loading || !isNoteValid() || originalNote.current === note.trim();

    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
              <CircularProgress />
            </div>
          ) : (
            <TripNotesField
              note={note}
              error={error}
              validationError={validationError}
              handleNoteChange={handleNoteChange}
              saving={saving}
              readOnly={readOnly}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={saving || loading} variant="outlined" color="primary">
            {translate('common.cancel')}
          </Button>
          {!readOnly && (
            <Button onClick={handleSave} color="primary" variant="contained" disabled={isSaveButtonDisabled}>
              {saving && <CircularProgress size={20} />}
              {!saving && translate('common.save')}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
);

TripNotesDetailsDialog.propTypes = {
  context: PropTypes.oneOf(['BILLING', 'ONGOING_TRIP']),
  readOnly: PropTypes.bool,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  tripId: PropTypes.string.isRequired,
  dataProvider: PropTypes.func,
  translate: PropTypes.func,
  dispatch: PropTypes.func,
};

const enhance = compose(
  withDataProvider,
  translateHOC
);

export default enhance(TripNotesDetailsDialog);
